import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useLiveCartPricing } from "@/hooks/useLiveCartPricing";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Navbar } from "@/components/sections/Navbar";
import { Footer } from "@/components/sections/Footer";
import { CheckoutProgressBar } from "@/components/checkout/CheckoutProgressBar";
import { OrderSummaryCard } from "@/components/checkout/OrderSummaryCard";
import { PaymentMethodSelector } from "@/components/checkout/PaymentMethodSelector";
import { TrustBadges } from "@/components/checkout/TrustBadges";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import productPackage from "@/assets/product-package.jpeg";

const checkoutSchema = z.object({
  firstName: z.string().min(2, "Minimaal 2 karakters vereist"),
  lastName: z.string().min(2, "Minimaal 2 karakters vereist"),
  email: z.string().email("Ongeldig e-mailadres"),
  phone: z.string().regex(/^[\d\s\+\-\(\)]+$/, "Ongeldig telefoonnummer"),
  street: z.string().min(3, "Straatnaam vereist"),
  houseNumber: z.string().min(1, "Huisnummer vereist"),
  postalCode: z.string().regex(/^\d{4}\s?[A-Z]{2}$/i, "Ongeldige postcode (1234AB)"),
  city: z.string().min(2, "Plaats vereist"),
  country: z.string().default("Nederland"),
  notes: z.string().optional(),
  paymentMethod: z.enum(["ideal", "creditcard", "paypal", "klarna"]),
});

type CheckoutFormData = z.infer<typeof checkoutSchema>;

export const CheckoutPage = () => {
  const navigate = useNavigate();
  const { items: cartItems, clearCart } = useCart();
  const { items: liveItems, priceChanges, total, isLoading: pricesLoading, hasPriceChanges } = useLiveCartPricing(cartItems);
  const [currentStep] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState("ideal");
  const [isProcessing, setIsProcessing] = useState(false);

  // Redirect if cart is empty
  useEffect(() => {
    if (cartItems.length === 0) {
      navigate("/producten");
    }
  }, [cartItems, navigate]);

  const form = useForm<CheckoutFormData>({
    resolver: zodResolver(checkoutSchema),
    defaultValues: {
      country: "Nederland",
      paymentMethod: "ideal",
    },
  });

  const onSubmit = async (data: CheckoutFormData) => {
    setIsProcessing(true);
    
    try {
      toast.loading("Je wordt doorverwezen naar de betaalpagina...");

      // Use live prices from database
      const { data: sessionData, error } = await supabase.functions.invoke(
        "create-checkout-session",
        {
          body: {
            items: liveItems.map(item => ({
              id: item.id,
              name: item.name,
              price: item.price, // Live price from database
              quantity: item.quantity,
              image: item.image,
            })),
            customerEmail: data.email,
            customerName: `${data.firstName} ${data.lastName}`,
            shippingAddress: {
              street: data.street,
              houseNumber: data.houseNumber,
              postalCode: data.postalCode,
              city: data.city,
              country: data.country,
            },
          },
        }
      );

      if (error) {
        console.error("Supabase function error:", error);
        throw error;
      }

      if (!sessionData?.url) {
        throw new Error("Geen checkout URL ontvangen");
      }

      // Redirect to Stripe Checkout
      window.location.href = sessionData.url;
    } catch (error: any) {
      console.error("Checkout error:", error);
      
      // Specific error messages
      let errorMessage = "Er ging iets mis bij het verwerken van je bestelling.";
      
      if (error.message?.includes("Not a valid URL")) {
        errorMessage = "Er is een probleem met de product afbeeldingen. Probeer het opnieuw.";
      } else if (error.message?.includes("network") || error.message?.includes("fetch")) {
        errorMessage = "Controleer je internetverbinding en probeer het opnieuw.";
      } else if (error.message?.includes("session")) {
        errorMessage = "Kon geen betaalsessie aanmaken. Probeer het later opnieuw.";
      }
      
      toast.dismiss();
      toast.error(errorMessage);
      setIsProcessing(false);
    }
  };

  return (
    <>
      <Navbar />
      
      <div className="min-h-screen bg-gradient-to-b from-background to-emerald-50/30 pt-32 pb-20">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="font-serif text-4xl md:text-5xl font-bold text-gold mb-4 text-glow">
              Checkout
            </h1>
            <p className="text-muted-foreground">
              Voltooi je bestelling in een paar eenvoudige stappen
            </p>
          </div>

          {/* Progress Bar */}
          <CheckoutProgressBar currentStep={currentStep} />

          {/* Price Change Alert */}
          {hasPriceChanges && (
            <Alert className="mt-8 border-orange-500 bg-orange-50">
              <AlertCircle className="h-4 w-4 text-orange-600" />
              <AlertDescription className="text-orange-900">
                <strong>Let op:</strong> De prijzen van sommige producten zijn gewijzigd sinds je ze aan je winkelwagen toevoegde:
                <ul className="mt-2 ml-4 list-disc">
                  {priceChanges.map((change) => (
                    <li key={change.itemId}>
                      {change.productName}: €{change.oldPrice.toFixed(2)} → €{change.newPrice.toFixed(2)}
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Main Checkout Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-12">
            {/* Left Column - Checkout Form */}
            <div className="lg:col-span-2 space-y-8">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                  {/* Customer Details */}
                  <div className="bg-card rounded-2xl border-2 border-gold/20 p-6 shadow-lg">
                    <h2 className="font-serif text-2xl font-bold text-gold mb-6">
                      Klantgegevens
                    </h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="firstName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Voornaam *</FormLabel>
                            <FormControl>
                              <Input placeholder="bijv. Emma" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="lastName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Achternaam *</FormLabel>
                            <FormControl>
                              <Input placeholder="bijv. van der Berg" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>E-mailadres *</FormLabel>
                            <FormControl>
                              <Input type="email" placeholder="bijv. emma@voorbeeld.nl" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Telefoonnummer *</FormLabel>
                            <FormControl>
                              <Input placeholder="bijv. 06 12345678" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  {/* Shipping Address */}
                  <div className="bg-card rounded-2xl border-2 border-gold/20 p-6 shadow-lg">
                    <h2 className="font-serif text-2xl font-bold text-gold mb-6">
                      Verzendadres
                    </h2>
                    
                    <div className="space-y-4">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="col-span-2">
                          <FormField
                            control={form.control}
                            name="street"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Straat *</FormLabel>
                                <FormControl>
                                  <Input placeholder="bijv. Hoofdstraat" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        <FormField
                          control={form.control}
                          name="houseNumber"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Nummer *</FormLabel>
                              <FormControl>
                                <Input placeholder="12A" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="postalCode"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Postcode *</FormLabel>
                              <FormControl>
                                <Input placeholder="1234AB" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="city"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Plaats *</FormLabel>
                              <FormControl>
                                <Input placeholder="bijv. Amsterdam" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="country"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Land *</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="Nederland">Nederland</SelectItem>
                                <SelectItem value="België">België</SelectItem>
                                <SelectItem value="Duitsland">Duitsland</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="notes"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Opmerkingen (optioneel)</FormLabel>
                            <FormControl>
                              <Textarea 
                                placeholder="Speciale instructies voor bezorging..."
                                className="resize-none"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  {/* Payment Method */}
                  <div className="bg-card rounded-2xl border-2 border-gold/20 p-6 shadow-lg">
                    <h2 className="font-serif text-2xl font-bold text-gold mb-6">
                      Betaalmethode
                    </h2>
                    
                    <FormField
                      control={form.control}
                      name="paymentMethod"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <PaymentMethodSelector 
                              value={paymentMethod}
                              onChange={(value) => {
                                setPaymentMethod(value);
                                field.onChange(value);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Submit Button - Mobile */}
                  <div className="lg:hidden">
                    <Button 
                      type="submit" 
                      className="btn-hero w-full text-lg py-6"
                      disabled={isProcessing || pricesLoading}
                    >
                      {isProcessing ? "Verwerken..." : "Naar Betalen"}
                    </Button>
                  </div>
                </form>
              </Form>
            </div>

            {/* Right Column - Order Summary */}
            <div className="lg:col-span-1">
              <div className="sticky top-32 space-y-6">
                <OrderSummaryCard items={liveItems} />
                
                {/* Submit Button - Desktop */}
                <Button 
                  onClick={form.handleSubmit(onSubmit)}
                  className="btn-hero w-full text-lg py-6 hidden lg:flex"
                  disabled={isProcessing || pricesLoading}
                >
                  {isProcessing ? "Verwerken..." : "Naar Betalen"}
                </Button>

                {/* Trust Badges */}
                <TrustBadges />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};
